import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarDays } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { DataTable, type Column } from '@/components/tables/DataTable';
import { FilterSelect, ListPage } from '@/components/tables/ListPage';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { useTableQuery } from '@/hooks/useTableQuery';
import { leaveRequestsApi, leaveTypesApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import { useAuth } from '@/features/auth/useAuth';
import { PERMISSIONS } from '@/types/permissions';
import type { LeaveRequest, LeaveRequestStatus } from '@/types/hr';
import { formatDate, orDash } from '@/utils/format';
import { Days, LeaveStatusBadge } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';
import { LeaveRequestDialog } from './LeaveRequestDialog';
import { LeaveDecisionDialog } from './LeaveDecisionDialog';

const STATUSES: { value: LeaveRequestStatus; label: string }[] = [
  { value: 'SUBMITTED', label: 'Awaiting manager' },
  { value: 'MANAGER_APPROVED', label: 'Awaiting HR' },
  { value: 'HR_APPROVED', label: 'Approved' },
  { value: 'TAKEN', label: 'Taken' },
  { value: 'REJECTED', label: 'Refused' },
  { value: 'CANCELLED', label: 'Withdrawn' },
];

/**
 * Every leave request in the school.
 *
 * <p>The two decision buttons are shown against the stage a request is actually
 * at, rather than always: a request awaiting HR must not offer a manager
 * decision, because the server would refuse it and the user would have learned
 * nothing from trying.
 */
export function LeaveRequestsPage() {
  const { can } = useAuth();
  const toast = useToast();
  const handleApiError = useApiErrorHandler();
  const queryClient = useQueryClient();
  const table = useTableQuery('startDate,desc');

  const [status, setStatus] = useState<LeaveRequestStatus | ''>('');
  const [typeId, setTypeId] = useState('');
  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [filing, setFiling] = useState(false);
  const [deciding, setDeciding] = useState<{ request: LeaveRequest; stage: 'MANAGER' | 'HR' } | null>(
    null,
  );

  const types = useQuery({
    queryKey: queryKeys.leaveTypes.list(false),
    queryFn: () => leaveTypesApi.list(false),
  });

  const params = {
    ...table.params,
    ...(status === '' ? {} : { status: [status] }),
    ...(typeId === '' ? {} : { leaveTypeId: typeId }),
    ...(employee === null ? {} : { employeeId: employee.id }),
  };

  const requests = useQuery({
    queryKey: queryKeys.leaveRequests.list(params),
    queryFn: () => leaveRequestsApi.list(params),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: queryKeys.leaveRequests.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.leaveBalances.all });
    void queryClient.invalidateQueries({ queryKey: queryKeys.hr.dashboard });
  };

  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      leaveRequestsApi.cancel(id, reason),
    onSuccess: (request) => {
      toast.success(`${request.requestNumber} withdrawn`);
      invalidate();
    },
    onError: (error) => handleApiError(error),
  });

  // The server lets HR and the employee's own supervisor read the evidence;
  // anyone else is told the request does not exist.
  const mayReadEvidence =
    can(PERMISSIONS.LEAVE_REQUEST_HR_APPROVE) || can(PERMISSIONS.LEAVE_REQUEST_APPROVE);

  const downloadEvidence = useMutation({
    mutationFn: (request: LeaveRequest) =>
      leaveRequestsApi.downloadDocument(request.id, request.documentFilename),
    onError: (error) => handleApiError(error),
  });

  const columns: Column<LeaveRequest>[] = [
    {
      key: 'requestNumber',
      header: 'Request',
      sortKey: 'requestNumber',
      render: (row) => (
        <span>
          <span className="block font-mono text-[12px] text-ink">{row.requestNumber}</span>
          <span className="block text-[11px] text-subtle">{orDash(row.leaveTypeName)}</span>
        </span>
      ),
    },
    {
      key: 'employee',
      header: 'Employee',
      render: (row) => (
        <span>
          <span className="block text-sm text-ink">{orDash(row.employeeName)}</span>
          <span className="block font-mono text-[11px] text-subtle">
            {orDash(row.employeeNumber)}
          </span>
        </span>
      ),
    },
    {
      key: 'dates',
      header: 'Dates',
      sortKey: 'startDate',
      render: (row) => (
        <span className="text-sm text-ink">
          {formatDate(row.startDate)}
          {row.startDate !== row.endDate && <> → {formatDate(row.endDate)}</>}
          {row.halfDay && <span className="ml-1 text-[11px] text-muted">(half day)</span>}
        </span>
      ),
    },
    {
      key: 'days',
      header: 'Days',
      sortKey: 'days',
      className: 'text-right',
      render: (row) => <Days value={row.days} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortKey: 'status',
      render: (row) => <LeaveStatusBadge status={row.status} />,
    },
    {
      key: 'actions',
      header: '',
      className: 'text-right',
      render: (row) => (
        <span className="flex flex-wrap justify-end gap-1.5">
          {mayReadEvidence && row.documentFilename !== undefined && (
            <Button
              size="sm"
              variant="ghost"
              title={row.documentFilename}
              loading={downloadEvidence.isPending && downloadEvidence.variables?.id === row.id}
              onClick={() => downloadEvidence.mutate(row)}
            >
              Evidence
            </Button>
          )}
          {row.status === 'SUBMITTED' && can(PERMISSIONS.LEAVE_REQUEST_APPROVE) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDeciding({ request: row, stage: 'MANAGER' })}
            >
              Decide
            </Button>
          )}
          {row.status === 'MANAGER_APPROVED' && can(PERMISSIONS.LEAVE_REQUEST_HR_APPROVE) && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => setDeciding({ request: row, stage: 'HR' })}
            >
              HR decision
            </Button>
          )}
          {/*
            Withdrawal is offered only where it can succeed: approved leave that
            has already begun is refused by the server, because some of it has
            been taken.
          */}
          {can(PERMISSIONS.LEAVE_REQUEST_CANCEL) &&
            !['CANCELLED', 'REJECTED', 'TAKEN'].includes(row.status) &&
            new Date(row.startDate) > new Date() && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const reason = window.prompt(`Why is ${row.requestNumber} being withdrawn?`);
                  if (reason !== null && reason.trim() !== '') {
                    cancel.mutate({ id: row.id, reason: reason.trim() });
                  }
                }}
              >
                Withdraw
              </Button>
            )}
        </span>
      ),
    },
  ];

  return (
    <>
      <ListPage
        title="Leave Requests"
        description="Every request in the school, and where each one has got to."
        icon={<CalendarDays className="h-5 w-5" aria-hidden="true" />}
        action={
          can(PERMISSIONS.LEAVE_REQUEST_HR_APPROVE) ? (
            <Button onClick={() => setFiling(true)}>File on behalf</Button>
          ) : undefined
        }
        filters={
          <>
            <FilterSelect
              label="Status"
              value={status}
              onChange={(value) => {
                setStatus(value);
                table.setPage(0);
              }}
              options={STATUSES}
            />
            <FilterSelect
              label="Type"
              value={typeId}
              onChange={(value) => {
                setTypeId(value);
                table.setPage(0);
              }}
              options={(types.data ?? []).map((type) => ({ value: type.id, label: type.name }))}
            />
          </>
        }
        pagination={requests.data?.pagination}
        onPageChange={table.setPage}
        onSizeChange={table.setSize}
      >
        <div className="border-b border-line px-4 py-3">
          <div className="max-w-sm">
            <EmployeePicker
              value={employee}
              onChange={(picked) => {
                setEmployee(picked);
                table.setPage(0);
              }}
              label="Filter by employee"
              required={false}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          rows={requests.data?.data}
          rowKey={(row) => row.id}
          loading={requests.isPending}
          error={requests.error}
          onRetry={() => void requests.refetch()}
          sort={table.sort}
          onSort={table.toggleSort}
          emptyTitle="No leave requests"
          emptyDescription="Nothing matches these filters."
        />
      </ListPage>

      {filing && (
        <LeaveRequestDialog
          mode="ON_BEHALF"
          onClose={() => setFiling(false)}
          onFiled={() => {
            setFiling(false);
            invalidate();
          }}
        />
      )}

      {deciding !== null && (
        <LeaveDecisionDialog
          request={deciding.request}
          stage={deciding.stage}
          onClose={() => setDeciding(null)}
          onDecided={() => {
            setDeciding(null);
            invalidate();
          }}
        />
      )}
    </>
  );
}
